import type { ElementType } from 'react';
import styled, { css, type Keyframes, keyframes } from 'styled-components';

import { BasicParagraph, style } from '@genially/design-system';
import DisplayDownIcon from '@genially/icons/lib/display-down/DisplayDownIcon';
import DisplayUpIcon from '@genially/icons/lib/display-up/DisplayUpIcon';

import { Transition } from './types/Transition';

export const Wrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: size.units(1),
  flex: '1 1 0',
}));

export const PositionWrapper = styled.div(({ theme: { size } }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: size.units(0.5),
}));

export const StyledBasicParagraph = styled(BasicParagraph)(({ theme: { color } }) => ({
  color: color.content.reversed.default(),
}));

const puffInAnimation = keyframes({
  '0%': {
    opacity: 0,
    filter: 'blur(4px)',
    transform: 'scale(2)',
  },

  '100%': {
    opacity: 1,
    filter: 'blur(0px)',
    transform: 'scale(1)',
  },
});

const fadeInAnimation = keyframes({
  '0%': {
    opacity: 0,
    filter: 'blur(4px)',
  },

  '100%': {
    opacity: 1,
    filter: 'blur(0px)',
  },
});

const withAnimation = (Component: ElementType, animation: Keyframes) =>
  styled(Component)<{ $activeAnimation: boolean; $delayAnimation?: number }>(
    ({ $activeAnimation, $delayAnimation = 0 }) =>
      $activeAnimation &&
      css`
        animation: ${animation} 0.7s cubic-bezier(0.47, 0, 0.745, 0.715)
          ${$delayAnimation}s both;
      `,
  );

interface UserAliasAvatarWrapperProps {
  $transition?: Transition;
}

export const UserAliasAvatarWrapper = withAnimation(
  styled.div<UserAliasAvatarWrapperProps>(({ $transition }) => {
    let transform = {};

    if ($transition === Transition.Enter) {
      transform = {
        transform: 'scale(1)',
        transitionTimingFunction: 'ease-in',
      };
    } else if ($transition === Transition.Exit) {
      transform = {
        transform: 'scale(0)',
        transitionTimingFunction: 'ease-out',
      };
    }

    return {
      transition: 'transform 0.3s',
      ...transform,
    };
  }),
  puffInAnimation,
);

interface UserAliasWrapperProps {
  $transition?: Transition;
}

export const UserAliasWrapper = withAnimation(
  styled.div<UserAliasWrapperProps>(({ $transition }) => {
    let transform = {};

    if ($transition === Transition.Enter) {
      transform = {
        opacity: 1,
        transitionTimingFunction: 'ease-in',
      };
    } else if ($transition === Transition.Exit) {
      transform = {
        opacity: 0,
        transitionTimingFunction: 'ease-out',
      };
    }

    return {
      transition: 'opacity 0.3s',
      ...transform,
    };
  }),
  fadeInAnimation,
);

interface UserAliasProps {
  $hasSpaces?: boolean;
  $centered?: boolean;
}

export const UserAlias = styled(BasicParagraph)<UserAliasProps>(
  ({ theme: { color }, $hasSpaces, $centered }) => ({
    color: color.content.reversed.default(),
    textAlign: $centered ? 'center' : 'left',

    ...style.mixins.ellipsis(true),

    whiteSpace: $hasSpaces ? 'normal' : 'break-spaces',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflowWrap: 'anywhere',
  }),
);

interface ShowProps {
  $show: boolean;
}

export const StyledDisplayUpIcon = styled(DisplayUpIcon)<ShowProps>(
  ({ theme: { color, size, transition }, $show }) => ({
    color: color.content.success.default(),
    width: size.units(2),
    height: size.units(2),
    opacity: $show ? 1 : 0,
    transition: transition.standard('opacity'),
  }),
);

export const StyledDisplayDownIcon = styled(DisplayDownIcon)<ShowProps>(
  ({ theme: { color, size, transition }, $show }) => ({
    color: color.content.error.default(),
    width: size.units(2),
    height: size.units(2),
    opacity: $show ? 1 : 0,
    transition: transition.standard('opacity'),
  }),
);
